import React, { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { REIButton } from "../../components/ui";
import { hasArchistarActive, openArchistarLogin } from "../../utils/commonUtil";

type RowItem = {
  section: string;
  feature: string;
  consumer: string;
  commercial: string;
};

const data: RowItem[] = [
  { section: "", feature: "CONTRACT_TERM_HEADER", consumer: "", commercial: "" },
  { section: "", feature: "View Full Site Details", consumer: "x", commercial: "x" },
  { section: "", feature: "Government Planning Details", consumer: "x", commercial: "x" },
  { section: "", feature: "Government Planning Overlays", consumer: "x", commercial: "x" },
  { section: "", feature: "Permitted Land Use", consumer: "x", commercial: "x" },
  { section: "", feature: "Sales History", consumer: "x", commercial: "x" },
  { section: "", feature: "Property Valuation", consumer: "x", commercial: "x" },
  { section: "", feature: "Advanced Measuring Tool", consumer: "x", commercial: "x" },
  { section: "", feature: "National Planning Library", consumer: "x", commercial: "x" },
  { section: "", feature: "Visualise Data & Risk Layers", consumer: "x", commercial: "x" },
  { section: "", feature: "Heritage Layer", consumer: "x", commercial: "x" },
  { section: "", feature: "3D Building Layer", consumer: "", commercial: "x" },
  { section: "", feature: "Bushfire, Flood & Electricity Layer", consumer: "x", commercial: "x" },
  { section: "", feature: "Contour Layer", consumer: "x", commercial: "x" },
  { section: "", feature: "Max Building Height Layer", consumer: "x", commercial: "x" },

  { section: "Discover Profitable Opportunities", feature: "SECTION_HEADER", consumer: "", commercial: "" },
  { section: "", feature: "Access Site Finder Tool", consumer: "x", commercial: "x" },
  { section: "", feature: "Filter by Land Use & Zone", consumer: "x", commercial: "x" },
  { section: "", feature: "Filter by Lot Size", consumer: "x", commercial: "x" },
  { section: "", feature: "Filter by FSR & Height", consumer: "x", commercial: "x" },
  { section: "", feature: "Filter by Development Potential", consumer: "x", commercial: "x" },
  { section: "", feature: "Run Due Diligence Reports", consumer: "Max 5 per month", commercial: "Unlimited" },
  { section: "", feature: "Calculate Feasibility", consumer: "Basic", commercial: "Basic / Advanced" },
  { section: "", feature: "Quick Estimate", consumer: "x", commercial: "x" },

  { section: "Design buildings with Artificial Intelligence", feature: "SECTION_HEADER", consumer: "", commercial: "" },
  { section: "", feature: "Generate Unlimited Designs", consumer: "x", commercial: "x" },
  { section: "", feature: "Analyse Designs", consumer: "x", commercial: "x" },
  { section: "", feature: "Design a Precinct", consumer: "", commercial: "x" },
  { section: "", feature: "Export Designs", consumer: "", commercial: "x" },

  { section: "My Properties", feature: "SECTION_HEADER", consumer: "", commercial: "" },
  { section: "", feature: "Rapid Assessment - Assess 1000's of sites", consumer: "", commercial: "x" },
  { section: "", feature: "Multiple pipelines for property reviews", consumer: "", commercial: "x" },
  { section: "", feature: "Access to ongoing product enhancements", consumer: "", commercial: "x" },

  { section: "Access Rich Partner Data", feature: "SECTION_HEADER", consumer: "", commercial: "" },
  { section: "", feature: "Purchase Site Documents", consumer: "x", commercial: "x" },
  { section: "", feature: "Title Search", consumer: "NSW, VIC, QLD", commercial: "NSW, VIC, QLD" },
  { section: "", feature: "Market Listings", consumer: "x", commercial: "x" },
  { section: "", feature: "Domain Listings", consumer: "x", commercial: "x" },
  { section: "", feature: "Commercial Real Estate Listings", consumer: "x", commercial: "x" },
  { section: "", feature: "CoreLogic Listings", consumer: "x", commercial: "x" },
  { section: "", feature: "BCI Australia Listings", consumer: "", commercial: "x" },
  { section: "", feature: "Cordell Listings", consumer: "", commercial: "x" },
  { section: "", feature: "Nearmap Aerial Imagery", consumer: "", commercial: "x" },

  { section: "Usage & Support", feature: "SECTION_HEADER", consumer: "", commercial: "" },
  { section: "", feature: "Users", consumer: "1 User", commercial: "Multiple Users" },
  { section: "", feature: "Region", consumer: "National", commercial: "State Options" },
  { section: "", feature: "Support Level", consumer: "Basic", commercial: "Dedicated Account Manager" },
  { section: "", feature: "Support Options", consumer: "Email & Phone", commercial: "Email & Phone" },
  { section: "", feature: "Training Videos & Docs", consumer: "x", commercial: "x" },

  { section: "Unique Limitation", feature: "SECTION_HEADER", consumer: "", commercial: "" },
  { section: "", feature: "Maximum Lot Size for Assessment", consumer: "2,000 m²", commercial: "Unlimited" },

  { section: "", feature: "BUTTON_ROW", consumer: "", commercial: "" },
];

const renderCell = (value: string) => {
  if (value === "x") {
    return <i className="pi pi-check text-green-600 text-base" aria-label="Included" />;
  }
  if (!value) {
    return null;
  }
  return <span>{value}</span>;
};

const Archistar: React.FC = () => {
  const navigate = useNavigate();
  const opened = useRef(false);
  const hasAccess = hasArchistarActive();

  useEffect(() => {
    if (!hasAccess || opened.current) return;
    opened.current = true;
    openArchistarLogin();
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  }, [hasAccess, navigate]);

  const headerGroup = useMemo(() => {
    return (
      <ColumnGroup>
        <Row>
          <Column
            header={<div className=" !justify-items-center !text-center"></div>}
            headerClassName="!bg-white !text-black !font-semibold !border-y !border-l !border-gray-300"
          />
          <Column
            header={
              <>
                Archistar Consumer <br />
                Real Estate Investar Add-On
              </>
            }
            headerClassName="!bg-white !text-black text-sm md:text-base lg:text-lg !font-semibold !border-y !border-r !border-gray-300 !text-center !justify-items-center"
          />
          <Column
            header={
              <>
                Archistar Commercial <br />
                Archistar Direct
              </>
            }
            headerClassName="!bg-white !text-black !text-lg !font-semibold !border-y !border-r !border-gray-300 !justify-items-center !text-center"
          />
        </Row>
      </ColumnGroup>
    );
  }, []);

  const rowClassName = (row: RowItem) => {
    if (row.feature === "CONTRACT_TERM_HEADER" || row.feature === "SECTION_HEADER") {
      return "bg-[#008236] font-semibold text-white";
    }
    return "";
  };

  const featureBody = (row: RowItem) => {
    if (row.feature === "CONTRACT_TERM_HEADER") {
      return <span className="text-sm md:text-base lg:text-lg">Contract Term</span>;
    }
    if (row.feature === "SECTION_HEADER") {
      return <span className="text-sm md:text-base lg:text-lg">{row.section}</span>;
    }
    if (row.feature === "BUTTON_ROW") {
      return null;
    }
    return <span className="text-sm md:text-base">{row.feature}</span>;
  };

  const consumerBody = (row: RowItem) => {
    if (row.feature === "CONTRACT_TERM_HEADER") {
      return <span className="text-sm md:text-base lg:text-lg">Monthly Casual</span>;
    }
    if (row.feature === "SECTION_HEADER") {
      return null;
    }
    if (row.feature === "BUTTON_ROW") {
      return (
        <REIButton
          id="arch-btn-addon"
          classNames="border-0 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-1 px-4 md:py-2 md:px-6 rounded text-xs md:text-sm"
          label="ADD ON ARCHISTAR"
          onClick={() => {
            navigate("/archistar-upgrade");
          }}
        />
      );
    }
    return (
      <div className=" -m-[1rem] p-[1rem] flex items-center justify-center text-sm md:text-base">
        {renderCell(row.consumer)}
      </div>
    );
  };

  const commercialBody = (row: RowItem) => {
    if (row.feature === "CONTRACT_TERM_HEADER") {
      return <span className="text-sm md:text-base lg:text-lg">Flexible</span>;
    }
    if (row.feature === "SECTION_HEADER") {
      return null;
    }
    if (row.feature === "BUTTON_ROW") {
      return (
        <REIButton
          id="arch-btn-visit"
          classNames="border-0 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-1 px-4 md:py-2 md:px-6 rounded text-xs md:text-sm"
          label="VISIT ARCHISTAR"
          onClick={() => {
            window.open("https://www.archistar.ai/pricing/", "_blank");
          }}
        />
      );
    }
    return <span className="text-sm md:text-base">{renderCell(row.commercial)}</span>;
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-r from-cyan-50 to-green-50 py-12 px-10 border-b-4 border-cyan-500">
        <div className="max-w-[1200px] mx-auto text-center">
          <div className="mb-6">
            <img
              src="https://info.realestateinvestar.com.au/hubfs/Archistar/Archistar%20Standard.png"
              alt="Archistar"
              className=""
            />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-black mb-2 mt-4 md:mb-3 md:mt-3 tracking-wide">
            Archistar Add-On
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
            Real Estate Investar has partnered with Archistar to offer members exclusive access to the Archistar
            "Add-On" subscription for just <span className="font-bold text-cyan-600">$99/month</span>. This national
            access includes all the features in Archistar consumer table below
          </p>

          <button
            id="arch-btn-watchdemo"
            data-testid="arch-btn-watchdemo"
            onClick={() =>
              document.getElementById("video-section")?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            className="inline-flex items-center gap-3 bg-white border-2 border-cyan-500 text-cyan-700 hover:bg-cyan-600 hover:text-white hover:border-cyan-600 font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            <i className="pi pi-play-circle text-2xl"></i>
            <span className="text-lg">Watch Demo Video</span>
          </button>
        </div>
      </div>

      <div className="py-12 px-10 bg-gray-100">
        <div className="max-w-[1200px] mx-auto">
          <div className="mb-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-3 flex items-center justify-center gap-3">
              <i className="pi pi-table text-cyan-600"></i>
              Compare Plans
            </h2>
            <p className="text-gray-600 text-sm md:text-base">
              Choose the perfect Archistar plan for your property development needs
            </p>
          </div>

          <div className="overflow-auto rounded-lg border-2 border-cyan-200 shadow-lg">
            <DataTable
              value={data}
              className="w-full"
              showGridlines
              headerColumnGroup={headerGroup}
              rowClassName={rowClassName}
            >
              <Column
                field="feature"
                header="Feature"
                body={featureBody}
                className="text-left text-sm md:text-base lg:text-lg"
                headerClassName="!border-0 bg-[#008236] text-green-900 font-semibold !text-center !justify-content-center text-xs md:text-sm"
              />
              <Column
                field="consumer"
                header="Archistar Consumer / REI Add-On"
                body={consumerBody}
                className="!text-center text-sm md:text-base lg:text-lg"
                headerClassName="!border-0 !bg-[#008236] text-green-900 font-semibold text-xs md:text-sm"
                bodyClassName={(row: RowItem) => {
                  if (
                    row.feature === "CONTRACT_TERM_HEADER" ||
                    row.feature === "SECTION_HEADER" ||
                    row.feature === "BUTTON_ROW"
                  ) {
                    return "";
                  }
                  return "!bg-gray-100";
                }}
              />
              <Column
                field="commercial"
                header="Archistar Commercial / Direct"
                body={commercialBody}
                className="text-center text-sm md:text-base lg:text-lg"
                headerClassName="!border-0 !bg-[#008236] text-green-900 font-semibold text-xs md:text-sm"
              />
            </DataTable>
          </div>
        </div>
      </div>

      <div id="video-section" className=" mb-5">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl text-black md:text-4xl lg:text-5xl font-bold mb-4">Watch Archistar Demo</h2>
            <p className="text-gray-700 text-lg md:text-xl font-medium max-w-2xl mx-auto leading-relaxed">
              See Archistar in action and discover how it transforms property development analysis
            </p>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-2xl blur-xl opacity-30 animate-pulse"></div>
            <div className="relative bg-white rounded-xl shadow-2xl overflow-hidden ">
              <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  src="https://fast.wistia.net/embed/iframe/npguzy8riw"
                  title="Archistar Demo"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Archistar;
